import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Tag, Modal, Descriptions, Input } from 'antd';
import { CheckOutlined, CloseOutlined, AuditOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { renderDateTime } from './utils';

const { TextArea } = Input;

interface Expert {
  id: number;
  expert_no: string;
  expert_name: string;
  gender: string;
  phone: string;
  expert_type: string;
  work_unit: string;
  industry: string;
  title: string;
  position: string;
  specialty: string;
  id_card: string;
  bank_name: string;
  bank_account: string;
  remark: string;
  review_status: string;
  create_time: string;
}

// 格式化身份证号：在第6、14位后分别加空格
const formatIdCard = (idCard: string | null | undefined): string => {
  if (!idCard) return '-';
  if (idCard.length !== 18) return idCard;
  return `${idCard.slice(0, 6)} ${idCard.slice(6, 14)} ${idCard.slice(14)}`;
};

export default function AccountExpertAudit() {
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState<Expert | null>(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingExpert, setRejectingExpert] = useState<Expert | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchExperts();
  }, []);

  const fetchExperts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/experts/pending', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setExperts(resp.data || []);
      } else {
        message.error(resp?.error || '获取待审核专家列表失败');
      }
    } catch (err) {
      message.error('获取待审核专家列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (record: Expert) => {
    setSelectedExpert(record);
    setDetailModalVisible(true);
  };

  const handleApprove = async (id: number, phone: string, closeModal: boolean = false) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/experts/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ phone }),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('审核通过');
        if (closeModal) {
          setDetailModalVisible(false);
          setSelectedExpert(null);
        }
        fetchExperts();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 打开拒绝原因弹窗
  const openRejectModal = (expert: Expert) => {
    setRejectingExpert(expert);
    setRejectReason('');
    setRejectModalVisible(true);
  };

  // 确认拒绝
  const handleRejectConfirm = async () => {
    if (!rejectingExpert) return;
    if (!rejectReason.trim()) {
      message.error('请填写拒绝原因');
      return;
    }
    if (rejectReason.length > 50) {
      message.error('拒绝原因不能超过50个字');
      return;
    }
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/experts/${rejectingExpert.id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ phone: rejectingExpert.phone, reason: rejectReason }),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('已拒绝');
        setRejectModalVisible(false);
        setRejectingExpert(null);
        setRejectReason('');
        setDetailModalVisible(false);
        setSelectedExpert(null);
        fetchExperts();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnsType<Expert> = [
    {
      title: '专家证号',
      dataIndex: 'expert_no',
      key: 'expert_no',
      width: 130,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text}</span>
    },
    {
      title: '姓名',
      dataIndex: 'expert_name',
      key: 'expert_name',
      width: 100,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 60,
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
    },
    {
      title: '专家类别',
      dataIndex: 'expert_type',
      key: 'expert_type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === '技术类' ? 'blue' : 'green'}>{type}</Tag>
      )
    },
    {
      title: '工作单位',
      dataIndex: 'work_unit',
      key: 'work_unit',
      width: 180,
      ellipsis: true,
    },
    {
      title: '职称',
      dataIndex: 'title',
      key: 'title',
      width: 120,
    },
    {
      title: '申请时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 160,
      render: renderDateTime,
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            查看详情
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => handleApprove(record.id, record.phone)}
            style={{ background: '#10b981', borderColor: '#10b981' }}
            size="small"
          >
            通过
          </Button>
          <Button
            danger
            icon={<CloseOutlined />}
            onClick={() => openRejectModal(record)}
            size="small"
          >
            拒绝
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <AuditOutlined />
          </div>
          <div>
            <h2>评审人员审核</h2>
            <p>审核待审评审人员申请</p>
          </div>
        </div>
        
        <Button 
          icon={<ReloadOutlined />} 
          onClick={fetchExperts}
          style={{ borderRadius: 10 }}
        >
          刷新
        </Button>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{experts.length}</span> 条待审核
        </div>
        
        <Table
          columns={columns}
          dataSource={experts}
          loading={loading}
          rowKey="id"
          className="admin-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      {/* 详情弹窗 */}
      <Modal
        title="评审人员详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={
          selectedExpert ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => setDetailModalVisible(false)}>关闭</Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={submitting}
                onClick={() => handleApprove(selectedExpert.id, selectedExpert.phone, true)}
                style={{ background: '#10b981', borderColor: '#10b981' }}
              >
                通过
              </Button>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => openRejectModal(selectedExpert)}
              >
                拒绝
              </Button>
            </div>
          ) : null
        }
        width="calc(800px + 15vw)"
      >
        {selectedExpert && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="工作单位" span={2}>{selectedExpert.work_unit || '-'}</Descriptions.Item>
            <Descriptions.Item label="专家证号">{selectedExpert.expert_no || '-'}</Descriptions.Item>
            <Descriptions.Item label="专家姓名">{selectedExpert.expert_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="性别">{selectedExpert.gender || '-'}</Descriptions.Item>
            <Descriptions.Item label="行业">{selectedExpert.industry || '-'}</Descriptions.Item>
            <Descriptions.Item label="职称">{selectedExpert.title || '-'}</Descriptions.Item>
            <Descriptions.Item label="职务">{selectedExpert.position || '-'}</Descriptions.Item>
            <Descriptions.Item label="电话">{selectedExpert.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="专家类别">
              {selectedExpert.expert_type ? (
                <Tag color={selectedExpert.expert_type === '技术类' ? 'blue' : 'green'}>
                  {selectedExpert.expert_type}
                </Tag>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="专业" span={2}>{selectedExpert.specialty || '-'}</Descriptions.Item>
            <Descriptions.Item label="身份证号">
              <span style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>
                {formatIdCard(selectedExpert.id_card)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="开户银行">{selectedExpert.bank_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="银行账号" span={2}>
              <span style={{ fontFamily: 'monospace' }}>{selectedExpert.bank_account || '-'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="备注" span={2}>{selectedExpert.remark || '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      
      {/* 拒绝原因弹窗 */}
      <Modal
        title="拒绝原因"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectingExpert(null);
          setRejectReason('');
        }}
        onOk={handleRejectConfirm}
        okText="确定"
        cancelText="取消"
        confirmLoading={submitting}
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 8, color: '#666' }}>
          请填写拒绝原因（不超过50个字）：
        </div>
        <TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="请输入拒绝原因"
          maxLength={50}
          showCount
          rows={4}
        />
      </Modal>
    </div>
  );
}

